// Single axios client for the v2 tickets backend. Every booking-flow
// call in this app should go through here so the Bearer token,
// base URL, and request/response shape stay consistent.
//
// Wire-up:
//   - Base URL comes from `TICKETS_API_URL` in `@/config/config.js`
//     (env-overridable via `VUE_APP_TICKETS_API_URL` at build time).
//   - The request interceptor attaches the guest / passenger JWT from
//     sessionStorage so the same middleware on the server gates the
//     whole flow uniformly.
//   - The response interceptor unwraps the standard envelope so
//     callers get `data` back directly. Non-2xx responses propagate
//     the AxiosError untouched so call sites can inspect `code` and
//     `errors.conflicts[]` etc.

import axios from "axios";
import { TICKETS_API_URL } from "@/config/config";

// Where we stash the v2 JWT (mint via /auth/guest-session on boot,
// upgrade via /auth/login). Same key everywhere on the site.
export const TICKETS_TOKEN_STORAGE_KEY = "tickets_v2_token";

const apiV2 = axios.create({
  baseURL: TICKETS_API_URL,
  // When axios times out it calls xhr.abort(), which the browser network tab
  // reports as "(canceled)" with 0 bytes — indistinguishable from a manual
  // cancel. The schedules search can be slow when the API server talks to a
  // distant DB, so a 20s cap was aborting requests that the server would have
  // answered fine (Postman, with no timeout, succeeds). 60s is the safety net;
  // the real fix is server-side query speed.
  timeout: 60000,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

apiV2.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem(TICKETS_TOKEN_STORAGE_KEY);
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// Pass the envelope `{ ok, code, message, data?, errors? }` through
// as-is on success — call sites read whichever fields they need.
apiV2.interceptors.response.use(
  (r) => r,
  (err) => {
    // Surface a readable label to the console; call sites still get
    // the original error object via the rejected promise.
    const status = err?.response?.status;
    const path = err?.config?.url;
    // eslint-disable-next-line no-console
    console.warn(`[apiV2] ${err?.config?.method?.toUpperCase()} ${path} → ${status ?? "no-response"}`);
    return Promise.reject(err);
  }
);

export default apiV2;
